import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder"
import { Stack, styled, Typography } from "@mui/material"
import { getIdsFromGeocoderResult, type GeocoderResult } from "@phc/common"
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react"
import { MAPBOX_ACCESS_TOKEN } from "../../../utils/env"
import {
  isAdminBoundaryFromPlaceType,
  placeTypeStringToEnum,
} from "../../../utils/helpers/assetHelper"
import { BORDER_RADIUS, extraColors, theme } from "../../../utils/theme"

const StyledGeocoder = styled("div")(() => ({
  "& .mapboxgl-ctrl-geocoder": {
    zIndex: 2,
    borderRadius: BORDER_RADIUS,
    boxShadow: "none",
    fontFamily: theme.typography.fontFamily,
    fontSize: 16,
    color: extraColors.medium,
    maxWidth: "unset",
    width: "100%",
    position: "relative",
    backgroundColor: "transparent",

    "& .suggestions-wrapper": {
      position: "absolute",
      top: "40px",
      width: "100%",
      marginTop: 8,
      cursor: "pointer",
      zIndex: 5,
      backgroundColor: extraColors.dark10,
      border: `1px solid ${extraColors.translucentWhite}`,
      borderTop: "none",
      borderBottomLeftRadius: BORDER_RADIUS,
      borderBottomRightRadius: BORDER_RADIUS,
      overflow: "hidden",
      color: extraColors.white,
      "& ul": {
        margin: 0,
        padding: 0,
        listStyle: "none",
      },
      "& li": {
        padding: "10px 12px",
      },
      "& li:hover, & li.active": {
        backgroundColor: extraColors.status.navyLightTransparent,
      },
      "& .mapboxgl-ctrl-geocoder--suggestion:hover *": {
        textTransform: "underline",
      },
      "& .mapboxgl-ctrl-geocoder--suggestion": {
        display: "flex",
        flexWrap: "wrap",
        columnGap: 6,
        rowGap: 0,
        alignItems: "baseline",
        whiteSpace: "normal",
      },
      "& .mapboxgl-ctrl-geocoder--single-line": {
        whiteSpace: "normal",
        wordBreak: "break-word",
      },
      "& .mapboxgl-ctrl-geocoder--suggestion-title, & .mapboxgl-ctrl-geocoder--suggestion-address":
        {
          minWidth: 0,
        },
      "& .mapboxgl-ctrl-geocoder--suggestion-title": {
        marginRight: 0,
      },
    },
    ".mapboxgl-ctrl-geocoder--icon": {
      color: extraColors.status.navyLight,
      fill: extraColors.status.navyLight,
      position: "absolute",
      left: 12,
      top: 24,
      transform: "translateY(-50%)",
      pointerEvents: "none",
    },
    ".mapboxgl-ctrl-geocoder--pin-right": {
      display: "none",
    },

    "& .mapboxgl-ctrl-geocoder--input": {
      border: `1px solid ${extraColors.disabled}`,
      boxShadow: "none",
      height: 48,
      borderRadius: BORDER_RADIUS,
      color: extraColors.white,
      paddingLeft: 40,
      paddingRight: 12,
      width: "100%",
      backgroundColor: extraColors.dark10,
      "&::placeholder": {
        color: extraColors.medium,
        opacity: 1,
      },
      "&:hover": {
        borderColor: extraColors.status.navyLightTransparent,
      },
      "&:focus": {
        border: `1px solid ${extraColors.status.navyLight}`,
        outline: "none",
      },
    },
  },
}))

export interface GeoLocationSearchInputProps {
  onChangeGeo?: (value?: GeocoderResult, locationIds?: string[]) => void
  setLocationId?: (value?: string) => void
  placeholder?: string
  setIsAdminBoundary?: (value: boolean) => void
  setName?: React.Dispatch<React.SetStateAction<string>>
  helperText?: string
}

export const AssetGeoLocationSearchInput = forwardRef<
  MapboxGeocoder,
  GeoLocationSearchInputProps
>(
  (
    {
      onChangeGeo,
      setLocationId,
      placeholder,
      setIsAdminBoundary,
      setName,
      helperText,
    },
    ref
  ) => {
    const [error, setError] = useState<string>()
    const geocoderRef = useRef(
      new MapboxGeocoder({
        accessToken: MAPBOX_ACCESS_TOKEN ?? "",
        placeholder: placeholder ?? "Location",
        // Render results as a single comma-separated line using Mapbox's place_name
        render: (item: any) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          const place_name = item?.place_name ?? ""
          return `
            <div class="mapboxgl-ctrl-geocoder--suggestion">
              <span class="mapboxgl-ctrl-geocoder--single-line">${place_name}</span>
            </div>
          `
        },
      })
    )
    // make the geocoder ref available to the parent component
    useImperativeHandle(ref, () => {
      return geocoderRef.current
    }, [])

    const mountGeocoder = useCallback(
      (node: HTMLDivElement | null) => {
        if (node !== null) {
          const geocoder = geocoderRef.current
          if (!node.firstChild) geocoder.addTo(node)

          const configureInputAttributes = () => {
            const input = node.querySelector<HTMLInputElement>(
              ".mapboxgl-ctrl-geocoder--input"
            )
            if (input) {
              input.setAttribute("type", "search")
              input.name = "location-search"
              input.setAttribute("inputmode", "search")
              // Disable saving/filling in password managers
              input.setAttribute("data-1p-ignore", "true")
              input.setAttribute("data-lpignore", "true")
            }
          }

          configureInputAttributes()

          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          geocoder.on("result", async (result: GeocoderResult) => {
            const locationIds = (
              await getIdsFromGeocoderResult(result, MAPBOX_ACCESS_TOKEN)
            ).map(f => f.properties.location_code ?? "")
            const location_id = locationIds[0]?.toString()
            onChangeGeo?.(result, locationIds)
            setLocationId?.(location_id)

            setIsAdminBoundary?.(
              isAdminBoundaryFromPlaceType(
                placeTypeStringToEnum(result.result?.place_type[0]?.toString())
              )
            )
            // If there's already a nickname set for the location, don't overwrite it.
            setName?.(prev =>
              !prev && result.result?.text ? result.result.text : prev
            )
          })

          geocoder.on("clear", () => {
            setError(undefined)
            onChangeGeo?.()
            setLocationId?.()
          })
        }
      },
      [onChangeGeo, setIsAdminBoundary, setLocationId, setName]
    )

    return (
      <>
        <StyledGeocoder ref={mountGeocoder} />
        {helperText && (
          <Typography
            variant="small1"
            color={extraColors.medium}
            sx={{ display: "block" }}
          >
            {helperText}
          </Typography>
        )}
        {error && (
          <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
            <Typography color="error">{error}</Typography>
          </Stack>
        )}
      </>
    )
  }
)

AssetGeoLocationSearchInput.displayName = "AssetGeoLocationSearchInput"
